import { OptionGroup } from "@/types";

const FLOAT_TOLERANCE = 0.000001;
const FRACTION_PRECISION = 6;

function roundPortionValue(value: number): number {
  return Number(value.toFixed(FRACTION_PRECISION));
}

/** A group is portioned ("meio a meio") exactly when it is priced by AVG or MAX, whatever its min/max or the backend flag. */
function isPortionOptionGroup(optionGroup: Pick<OptionGroup, "minPermitted" | "maxPermitted" | "priceMethod">): boolean {
  return optionGroup.priceMethod === "AVG" || optionGroup.priceMethod === "MAX";
}

/** Each selected option's share of the item (one of two picks = 0.5): the part of its whole price it costs. */
function toPortionShares(effectiveGroupQty: Record<string, number>, groupTarget: number): Record<string, number> {
  if (groupTarget <= 0) return {};
  return Object.fromEntries(
    Object.entries(effectiveGroupQty).map(([id, qty]) => [id, qty > 0 ? qty / groupTarget : 0]),
  );
}

function getPortionTarget(optionGroup: Pick<OptionGroup, "minPermitted" | "maxPermitted" | "priceMethod">): number | null {
  if (!isPortionOptionGroup(optionGroup)) return null;
  return optionGroup.maxPermitted;
}

function distributePortionQuantities(
  currentGroupQty: Record<string, number>,
  optionId: string,
  groupTarget: number,
): Record<string, number> {
  const currentQty = currentGroupQty[optionId] ?? 0;

  if (currentQty > 0) {
    return {
      ...currentGroupQty,
      [optionId]: currentQty + 1,
    };
  }

  const withSelection = {
    ...currentGroupQty,
    [optionId]: 1,
  };

  const selectedIds = Object.entries(withSelection)
    .filter(([, qty]) => qty > 0)
    .map(([id]) => id);

  if (selectedIds.length === 0) return withSelection;

  const nextGroupQty = { ...withSelection };
  const share = roundPortionValue(groupTarget / selectedIds.length);

  selectedIds.forEach((id) => {
    nextGroupQty[id] = share;
  });

  return nextGroupQty;
}

function resolveEffectivePortionQuantities(
  currentGroupQty: Record<string, number>,
  groupTarget: number,
): Record<string, number> {
  const selectedEntries = Object.entries(currentGroupQty)
    .filter(([, qty]) => qty > 0);

  if (selectedEntries.length === 0) return currentGroupQty;

  const selectedTotal = selectedEntries.reduce((acc, [, qty]) => acc + qty, 0)
  if (selectedTotal <= 0) return currentGroupQty

  const scaleFactor = groupTarget / selectedTotal

  return selectedEntries.reduce((acc, [id, qty]) => ({
    ...acc,
    [id]: roundPortionValue(qty * scaleFactor),
  }), { ...currentGroupQty });
}

function getPortionFractionBase(
  currentGroupQty: Record<string, number>,
  groupTarget: number,
): number {
  const totalSelected = Object.values(currentGroupQty)
    .filter((qty) => qty > 0)
    .reduce((acc, qty) => acc + qty, 0)

  if (totalSelected <= 0) return groupTarget

  return roundPortionValue(totalSelected)
}

function isValidPortionSelection(
  currentGroupQty: Record<string, number>,
  groupTarget: number,
): boolean {
  const selectedEntries = Object.entries(currentGroupQty)
    .filter(([, qty]) => qty > 0);

  if (selectedEntries.length === 0) return false;

  if (selectedEntries.length === 1) return true;

  const allAreSingleSelection = selectedEntries.every(([, qty]) => qty === 1);

  if (allAreSingleSelection) {
    return true;
  }

  const totalSelected = selectedEntries.reduce((acc, [, qty]) => acc + qty, 0);

  return totalSelected > 0 && totalSelected <= (groupTarget + FLOAT_TOLERANCE);
}

/** Whether a portion group lets the item go on: an optional group may stay empty, otherwise the picks must be valid. */
function isPortionGroupSatisfied(
  currentGroupQty: Record<string, number>,
  groupTarget: number,
  minPermitted: number,
): boolean {
  const hasSelection = Object.values(currentGroupQty).some((qty) => qty > 0);
  if (!hasSelection) return minPermitted <= 0;
  return isValidPortionSelection(currentGroupQty, groupTarget);
}

const PORTION_WIRE_PRECISION = 3;

/**
 * Fraction of one item each selected portion option covers (half = 0.5),
 * rounded to the backend's milli grid: the order wire shape for portion groups.
 */
function toPortionFractions(
  effectiveGroupQty: Record<string, number>,
  groupTarget: number,
): Record<string, number> {
  if (groupTarget <= 0) return {};
  return Object.fromEntries(
    Object.entries(effectiveGroupQty)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => [id, Number((qty / groupTarget).toFixed(PORTION_WIRE_PRECISION))]),
  );
}

/**
 * Rescales portion-group quantities read back from an order or a synced cart
 * (fractions of one item, or legacy slots) into slots summing to each target.
 */
function normalizePortionGroups(
  optionGroups: Pick<OptionGroup, "id" | "minPermitted" | "maxPermitted" | "priceMethod">[],
  quantitiesByGroup: Record<string, Record<string, number>>,
): Record<string, Record<string, number>> {
  const normalized = { ...quantitiesByGroup };
  for (const group of optionGroups) {
    const target = getPortionTarget(group);
    const quantities = normalized[group.id];
    if (!target || !quantities) continue;
    normalized[group.id] = resolveEffectivePortionQuantities(quantities, target);
  }
  return normalized;
}

function formatReducedFraction(numerator: number, denominator: number): string {
  if (denominator <= 0) return `${numerator}/${denominator}`;

  const normalizedNumerator = roundPortionValue(numerator);
  const normalizedDenominator = roundPortionValue(denominator);

  if (Math.abs(normalizedNumerator) < FLOAT_TOLERANCE) return `0/${normalizedDenominator}`;

  const ratio = Math.abs(normalizedNumerator / normalizedDenominator)
  const signal = normalizedNumerator < 0 ? -1 : 1

  const gcd = (a: number, b: number): number => {
    let left = Math.abs(a)
    let right = Math.abs(b)
    while (right !== 0) {
      const remainder = left % right
      left = right
      right = remainder
    }
    return left || 1
  }

  const MAX_UI_DENOMINATOR = 16
  const APPROX_TOLERANCE = 0.0005

  let bestNumerator = 0
  let bestDenominator = 1
  let smallestError = Number.POSITIVE_INFINITY

  for (let denominatorCandidate = 1; denominatorCandidate <= MAX_UI_DENOMINATOR; denominatorCandidate += 1) {
    const numeratorCandidate = Math.round(ratio * denominatorCandidate)
    const candidateRatio = numeratorCandidate / denominatorCandidate
    const error = Math.abs(candidateRatio - ratio)

    if (error < smallestError) {
      smallestError = error
      bestNumerator = numeratorCandidate
      bestDenominator = denominatorCandidate
    }
  }

  if (smallestError <= APPROX_TOLERANCE && bestNumerator > 0) {
    const divisor = gcd(bestNumerator, bestDenominator)
    return `${(bestNumerator / divisor) * signal}/${bestDenominator / divisor}`
  }

  const scaleFactor = 10 ** FRACTION_PRECISION
  const scaledNumerator = Math.round(Math.abs(normalizedNumerator) * scaleFactor)
  const scaledDenominator = Math.round(Math.abs(normalizedDenominator) * scaleFactor)
  const divisor = gcd(scaledNumerator, scaledDenominator)

  return `${(scaledNumerator / divisor) * signal}/${scaledDenominator / divisor}`
}

export {
  formatReducedFraction,
  distributePortionQuantities,
  getPortionFractionBase,
  getPortionTarget,
  isPortionGroupSatisfied,
  isPortionOptionGroup,
  isValidPortionSelection,
  normalizePortionGroups,
  resolveEffectivePortionQuantities,
  toPortionFractions,
  toPortionShares,
};